import { type AgentSessionResponse } from '@air/server-sdk';
import { reportErrorToBugsnag } from '@air/utils-error';

export const TOOL_CONSENT_ELICITATION_TYPE = 'tool_consent';
export const BINARY_CHOICE_ELICITATION_TYPE = 'binary_choice';
export const SINGLE_SELECT_ELICITATION_TYPE = 'single_select';
export const MULTI_SELECT_ELICITATION_TYPE = 'multi_select';
export const DESCRIBE_ELICITATION_TYPE = 'describe';
export const TEXT_FIELDS_ELICITATION_TYPE = 'text_fields';
export const STEPPER_ELICITATION_TYPE = 'stepper';
export const SEARCH_SELECT_ELICITATION_TYPE = 'search_select';
export const SOMETHING_ELSE_LABEL = 'Something else';
export const SOMETHING_ELSE_MAX_LENGTH = 280;

/**
 * Wire row shape from `@air/server-sdk` (`AgentSessionResponse.pendingElicitations`).
 * The SDK does not export a named elicitation type — derive it here and narrow at use sites
 * instead of re-parsing with Zod.
 */
export type AgentSessionElicitation = NonNullable<AgentSessionResponse['pendingElicitations']>[number];

/** SDK-typed elicitation row before runtime classification. */
export type UnknownElicitation = AgentSessionElicitation;

type SdkElicitationPayload = AgentSessionElicitation['payload'];

/**
 * Narrow an SDK elicitation row by `type` and payload. Uses `Omit` + intersection (not `Extract`)
 * because the SDK types `type` as `string`. Once server-sdk exports a discriminated union, switch to:
 * `Extract<AgentSessionElicitation, { type: TType }>` and derive payload/display from that branch.
 */
type NarrowAgentSessionElicitation<
  TType extends string,
  TPayload extends SdkElicitationPayload = SdkElicitationPayload,
> = Omit<AgentSessionElicitation, 'type' | 'payload'> & {
  type: TType;
  payload: TPayload;
};

/**
 * Agent-owned consent copy in `tool_consent` payloads (WORK-337). Not named in `@air/server-sdk` —
 * keep local until backend exports it on the elicitation discriminated union.
 */
export type AgentElicitationDisplaySampleItem =
  | { type: 'line'; text: string }
  | { type: 'change'; before: string; after: string }
  | { type: 'tags'; title: string; tags: string[] };

export type AgentElicitationDisplay = {
  title?: string;
  instruction?: string;
  summary?: string;
  sample?: AgentElicitationDisplaySampleItem[];
  truncated?: boolean;
};

/** Runtime-validated `tool_consent` payload fields (extends SDK `Record<string, unknown>`). */
export type ToolConsentElicitationPayload = SdkElicitationPayload & {
  toolName: string;
  toolCallId: string;
  arguments: Record<string, unknown>;
  display?: AgentElicitationDisplay;
};

export type ToolConsentElicitation = NarrowAgentSessionElicitation<
  typeof TOOL_CONSENT_ELICITATION_TYPE,
  ToolConsentElicitationPayload
>;

export type BinaryChoiceElicitationPayload = SdkElicitationPayload & {
  prompt: string;
  options: [string, string];
};

export type BinaryChoiceElicitation = NarrowAgentSessionElicitation<
  typeof BINARY_CHOICE_ELICITATION_TYPE,
  BinaryChoiceElicitationPayload
>;

export type SelectElicitationPayload = SdkElicitationPayload & {
  prompt: string;
  options: string[];
  /** Options already picked when the dock opens (a recommended default, pre-checked choices). */
  defaultIndexes?: number[];
  /** What the last option reads in a pick-one dock; the wire option stays Something else. Pick several ignores it. */
  otherLabel?: string;
};

export type SingleSelectElicitation = NarrowAgentSessionElicitation<
  typeof SINGLE_SELECT_ELICITATION_TYPE,
  SelectElicitationPayload
>;

export type MultiSelectElicitation = NarrowAgentSessionElicitation<
  typeof MULTI_SELECT_ELICITATION_TYPE,
  SelectElicitationPayload
>;

export type DescribeElicitationPayload = SdkElicitationPayload & {
  prompt: string;
  placeholder?: string;
};

export type DescribeElicitation = NarrowAgentSessionElicitation<
  typeof DESCRIBE_ELICITATION_TYPE,
  DescribeElicitationPayload
>;

export type TextFieldSpec = {
  label: string;
  placeholder?: string;
};

export type TextFieldsElicitationPayload = SdkElicitationPayload & {
  prompt: string;
  fields: TextFieldSpec[];
};

export type TextFieldsElicitation = NarrowAgentSessionElicitation<
  typeof TEXT_FIELDS_ELICITATION_TYPE,
  TextFieldsElicitationPayload
>;

export type StepperElicitationPayload = SdkElicitationPayload & {
  prompt: string;
  min: number;
  max: number;
  step: number;
  value: number;
};

export type StepperElicitation = NarrowAgentSessionElicitation<
  typeof STEPPER_ELICITATION_TYPE,
  StepperElicitationPayload
>;

export type SearchSelectElicitationPayload = SdkElicitationPayload & {
  prompt: string;
  options: string[];
  multiple: boolean;
  /** Options already picked when the dock opens. */
  defaultIndexes?: number[];
};

export type SearchSelectElicitation = NarrowAgentSessionElicitation<
  typeof SEARCH_SELECT_ELICITATION_TYPE,
  SearchSelectElicitationPayload
>;

export type ParsedElicitation =
  | { kind: 'tool_consent'; elicitation: ToolConsentElicitation }
  | { kind: 'binary_choice'; elicitation: BinaryChoiceElicitation }
  | { kind: 'single_select'; elicitation: SingleSelectElicitation }
  | { kind: 'multi_select'; elicitation: MultiSelectElicitation }
  | { kind: 'describe'; elicitation: DescribeElicitation }
  | { kind: 'text_fields'; elicitation: TextFieldsElicitation }
  | { kind: 'stepper'; elicitation: StepperElicitation }
  | { kind: 'search_select'; elicitation: SearchSelectElicitation }
  | { kind: 'unknown'; elicitation: UnknownElicitation }
  | { kind: 'invalid'; elicitation: null };

export type RenderableElicitation =
  | ToolConsentElicitation
  | BinaryChoiceElicitation
  | SingleSelectElicitation
  | MultiSelectElicitation
  | DescribeElicitation
  | TextFieldsElicitation
  | StepperElicitation
  | SearchSelectElicitation
  | UnknownElicitation;

export type ParseElicitationOptions = {
  /** When false, invalid payloads return `invalid` without notifying Bugsnag — for hot render paths. */
  reportInvalid?: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** Minimum wire shape shared by every pending elicitation row (`AgentSessionElicitation`). */
export const isRenderableElicitation = (raw: unknown): raw is AgentSessionElicitation =>
  isRecord(raw) &&
  typeof raw.id === 'string' &&
  typeof raw.type === 'string' &&
  typeof raw.createdAt === 'string' &&
  isRecord(raw.payload);

const isToolConsentPayload = (payload: SdkElicitationPayload): payload is ToolConsentElicitationPayload => {
  const record: Record<string, unknown> = payload;

  return typeof record.toolName === 'string' && typeof record.toolCallId === 'string' && isRecord(record.arguments);
};

export const isToolConsentElicitation = (elicitation: AgentSessionElicitation): elicitation is ToolConsentElicitation =>
  elicitation.type === TOOL_CONSENT_ELICITATION_TYPE && isToolConsentPayload(elicitation.payload);

const isBinaryChoicePayload = (payload: SdkElicitationPayload): payload is BinaryChoiceElicitationPayload => {
  const record: Record<string, unknown> = payload;

  if (typeof record.prompt !== 'string' || record.prompt.trim().length === 0) {
    return false;
  }

  if (!Array.isArray(record.options) || record.options.length !== 2) {
    return false;
  }

  const [primary, decline] = record.options;

  return (
    typeof primary === 'string' &&
    primary.trim().length > 0 &&
    typeof decline === 'string' &&
    decline.trim().length > 0 &&
    primary.trim() !== decline.trim()
  );
};

export const isBinaryChoiceElicitation = (
  elicitation: AgentSessionElicitation,
): elicitation is BinaryChoiceElicitation =>
  elicitation.type === BINARY_CHOICE_ELICITATION_TYPE && isBinaryChoicePayload(elicitation.payload);

const SELECT_MIN_OPTIONS = 2;
const SELECT_MAX_OPTIONS = 5;
const OTHER_LABEL_MAX_LENGTH = 40;

/** Absent, or whole numbers naming distinct options. */
const isDefaultIndexes = (value: unknown, optionCount: number): boolean =>
  value === undefined ||
  (Array.isArray(value) &&
    value.every((index) => typeof index === 'number' && Number.isInteger(index) && index >= 0 && index < optionCount) &&
    new Set(value).size === value.length);

const hasSelectOptionCount = (options: unknown): options is unknown[] =>
  Array.isArray(options) && options.length >= SELECT_MIN_OPTIONS && options.length <= SELECT_MAX_OPTIONS;

/** Missing is allowed. A present label has to be a short non-empty string. */
const isAbsentOrOtherLabel = (otherLabel: unknown) =>
  otherLabel === undefined ||
  (typeof otherLabel === 'string' &&
    otherLabel.trim().length > 0 &&
    otherLabel.trim().length <= OTHER_LABEL_MAX_LENGTH);

const isSelectPayload = (payload: SdkElicitationPayload): payload is SelectElicitationPayload => {
  const record: Record<string, unknown> = payload;

  if (typeof record.prompt !== 'string' || record.prompt.trim().length === 0) {
    return false;
  }

  if (!hasSelectOptionCount(record.options)) {
    return false;
  }

  const labels = record.options.map((option: unknown) => (typeof option === 'string' ? option.trim() : ''));

  if (labels.some((label) => label.length === 0) || new Set(labels).size !== labels.length) {
    return false;
  }

  if (!isDefaultIndexes(record.defaultIndexes, labels.length)) {
    return false;
  }

  if (!isAbsentOrOtherLabel(record.otherLabel)) {
    return false;
  }

  return labels[labels.length - 1] === SOMETHING_ELSE_LABEL;
};

export const isSingleSelectElicitation = (
  elicitation: AgentSessionElicitation,
): elicitation is SingleSelectElicitation =>
  elicitation.type === SINGLE_SELECT_ELICITATION_TYPE && isSelectPayload(elicitation.payload);

export const isMultiSelectElicitation = (elicitation: AgentSessionElicitation): elicitation is MultiSelectElicitation =>
  elicitation.type === MULTI_SELECT_ELICITATION_TYPE && isSelectPayload(elicitation.payload);

const PROMPT_MAX_LENGTH = 280;
const HINT_MAX_LENGTH = 80;
const FIELD_LABEL_MAX_LENGTH = 40;
const MAX_TEXT_FIELDS = 4;
const STEPPER_MAX_SPAN = 100;
const SEARCH_MAX_OPTIONS = 40;
const SEARCH_LABEL_MAX_LENGTH = 80;

const isNonEmptyString = (value: unknown, maxLength: number): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.trim().length <= maxLength;

const isWholeNumber = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value);

const isDescribePayload = (payload: SdkElicitationPayload): payload is DescribeElicitationPayload => {
  const record: Record<string, unknown> = payload;

  if (!isNonEmptyString(record.prompt, PROMPT_MAX_LENGTH)) {
    return false;
  }

  return record.placeholder === undefined || isNonEmptyString(record.placeholder, HINT_MAX_LENGTH);
};

export const isDescribeElicitation = (elicitation: AgentSessionElicitation): elicitation is DescribeElicitation =>
  elicitation.type === DESCRIBE_ELICITATION_TYPE && isDescribePayload(elicitation.payload);

const isTextFieldsPayload = (payload: SdkElicitationPayload): payload is TextFieldsElicitationPayload => {
  const record: Record<string, unknown> = payload;

  if (!isNonEmptyString(record.prompt, PROMPT_MAX_LENGTH) || !Array.isArray(record.fields)) {
    return false;
  }

  if (record.fields.length < 1 || record.fields.length > MAX_TEXT_FIELDS) {
    return false;
  }

  const labels = record.fields.map((field: unknown) => {
    if (!isRecord(field)) {
      return '';
    }

    const label = typeof field.label === 'string' ? field.label.trim() : '';
    const placeholderOk = field.placeholder === undefined || isNonEmptyString(field.placeholder, HINT_MAX_LENGTH);

    return placeholderOk && label.length > 0 && label.length <= FIELD_LABEL_MAX_LENGTH ? label : '';
  });

  return labels.every((label) => label.length > 0) && new Set(labels).size === labels.length;
};

export const isTextFieldsElicitation = (elicitation: AgentSessionElicitation): elicitation is TextFieldsElicitation =>
  elicitation.type === TEXT_FIELDS_ELICITATION_TYPE && isTextFieldsPayload(elicitation.payload);

const isStepperPayload = (payload: SdkElicitationPayload): payload is StepperElicitationPayload => {
  const record: Record<string, unknown> = payload;
  const { min, max, step, value } = record;

  if (
    !isNonEmptyString(record.prompt, PROMPT_MAX_LENGTH) ||
    !isWholeNumber(min) ||
    !isWholeNumber(max) ||
    !isWholeNumber(step)
  ) {
    return false;
  }

  if (!isWholeNumber(value) || min >= max || max - min > STEPPER_MAX_SPAN || step < 1) {
    return false;
  }

  return value >= min && value <= max && (value - min) % step === 0;
};

export const isStepperElicitation = (elicitation: AgentSessionElicitation): elicitation is StepperElicitation =>
  elicitation.type === STEPPER_ELICITATION_TYPE && isStepperPayload(elicitation.payload);

const isSearchSelectPayload = (payload: SdkElicitationPayload): payload is SearchSelectElicitationPayload => {
  const record: Record<string, unknown> = payload;

  if (
    !isNonEmptyString(record.prompt, PROMPT_MAX_LENGTH) ||
    typeof record.multiple !== 'boolean' ||
    !Array.isArray(record.options)
  ) {
    return false;
  }

  if (record.options.length < 1 || record.options.length > SEARCH_MAX_OPTIONS) {
    return false;
  }

  const labels = record.options.map((option: unknown) => (typeof option === 'string' ? option.trim() : ''));

  return (
    labels.every((label) => label.length > 0 && label.length <= SEARCH_LABEL_MAX_LENGTH) &&
    new Set(labels).size === labels.length &&
    isDefaultIndexes(record.defaultIndexes, labels.length)
  );
};

export const isSearchSelectElicitation = (
  elicitation: AgentSessionElicitation,
): elicitation is SearchSelectElicitation =>
  elicitation.type === SEARCH_SELECT_ELICITATION_TYPE && isSearchSelectPayload(elicitation.payload);

/** Classify an SDK-typed elicitation row by narrowing on `type` and payload shape. */
export const classifyElicitation = (elicitation: AgentSessionElicitation): ParsedElicitation => {
  if (isToolConsentElicitation(elicitation)) {
    return { kind: 'tool_consent', elicitation };
  }

  if (isBinaryChoiceElicitation(elicitation)) {
    return { kind: 'binary_choice', elicitation };
  }

  if (isSingleSelectElicitation(elicitation)) {
    return { kind: 'single_select', elicitation };
  }

  if (isMultiSelectElicitation(elicitation)) {
    return { kind: 'multi_select', elicitation };
  }

  if (isDescribeElicitation(elicitation)) {
    return { kind: 'describe', elicitation };
  }

  if (isTextFieldsElicitation(elicitation)) {
    return { kind: 'text_fields', elicitation };
  }

  if (isStepperElicitation(elicitation)) {
    return { kind: 'stepper', elicitation };
  }

  if (isSearchSelectElicitation(elicitation)) {
    return { kind: 'search_select', elicitation };
  }

  return { kind: 'unknown', elicitation };
};

/**
 * Guard untrusted wire payloads (e.g. WSS) with a minimal structural check, then classify.
 * Session GET responses are already SDK-typed — prefer `classifyElicitation` there.
 */
export const parseElicitation = (raw: unknown, options?: ParseElicitationOptions): ParsedElicitation => {
  if (!isRenderableElicitation(raw)) {
    if (options?.reportInvalid ?? true) {
      reportErrorToBugsnag({
        error: new Error('Elicitation payload could not be parsed'),
        context: 'parseElicitation',
      });
    }

    return { kind: 'invalid', elicitation: null };
  }

  return classifyElicitation(raw);
};
